import { useState } from "react";
import { BellIcon, CalendarIcon, Send as SendIcon } from "lucide-react";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faChessBoard, faChessKnight, faChessRook } from "@fortawesome/free-solid-svg-icons";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import clsx from "clsx";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { type HighlightArea, highlightPlugin, type RenderHighlightsProps, Trigger } from "@react-pdf-viewer/highlight";
import { Viewer } from "@react-pdf-viewer/core";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";

import { courtDataUpdatedMock, CourtEventsMockData } from "./mocks/courtEventsMockData";
import { Button } from "@/components/ui/button";
// Import the styles
import "@react-pdf-viewer/core/lib/styles/index.css";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Import styles
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import { ViewerWrapper } from "@/components/feature/pdf/viewerWrapper";
import { AllDatesTable } from "@/components/feature/tables/deadline_tracker/allDatesTable";
import { DeadlineReminderListTable } from "@/components/feature/tables/deadline_tracker/deadlineReminderListTable";
import { VerifyTable } from "@/components/feature/tables/deadline_tracker/verifyTable";
import { PreviewInvitesModal } from "@/components/feature/modal/Modal";
import { RoleToggle } from "@/components/feature/deadline_guardian/verification_view/ToggleButton";
// import RoleToggle from "@/app/(dashboard)/feature/verification_view/ToggleButton";
// import PreviewInvitesModal from "@/app/(dashboard)/feature/modal/Modal";
// import AllDatesTable from "@/app/(dashboard)/feature/tables/deadline_tracker/allDatesTable";
// import { DeadlineReminderListTable } from "@/app/(dashboard)/feature/tables/deadline_tracker/deadlineReminderListTable";
// import ViewerWrapper from "@/app/(dashboard)/feature/pdf/viewerWrapper";
// import { VerifyTable } from "@/app/(dashboard)/feature/tables/deadline_tracker/verifyTable";

library.add(faChessRook, faChessKnight, faChessBoard);

const tabClassName = (selected: boolean) =>
  selected
    ? "border-indigo-500 text-indigo-600"
    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700";
const iconClassName = (selected: boolean) => (selected ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500");

export const DeadlineGuardianView = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;

  const [areas, setAreas] = useState<HighlightArea[]>([]);
  const renderHighlights = (props: RenderHighlightsProps) => (
    <div>
      {areas
        .filter(area => area.pageIndex === props.pageIndex)
        .map((area, idx) => (
          <div
            key={idx}
            className="highlight-area"
            style={Object.assign(
              {},
              {
                background: "yellow",
                opacity: 0.4,
              },
              props.getCssProperties(area, props.rotation),
            )}
          />
        ))}
    </div>
  );
  const highlightPluginInstance = highlightPlugin({
    renderHighlights,
    trigger: Trigger.None,
  });
  const jumpToHighlightArea = (area: HighlightArea) => {
    highlightPluginInstance.jumpToHighlightArea(area);
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const handleHighlight = (highlightAreas: HighlightArea[], page: number) => {
    setAreas(highlightAreas);
    jumpToHighlightArea(highlightAreas[0]);
    setTimeout(() => {
      document.querySelector(`[data-page-number="${page + 1}"]`)?.scrollIntoView({ behavior: "instant" });
    }, 100);
  };

  return (
    <div className="mx-auto mb-12 p-4">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Pathfinder</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Tracker</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="shadow-xs mb-8 w-full p-4 pl-0">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900">Abacus Delta Ltd vs Clover Offices Ltd</h1>
        <p className="mt-4 max-w-3xl text-base text-gray-500">
          <b>Court:</b> COMMERCIAL COURT (QBD)
        </p>
        <p className="mt-4 max-w-3xl text-base text-gray-500">
          <b>Matter Id:</b> Abacus_2024_004
        </p>
      </div>
      <div className="mb-2 flex w-full items-center justify-between pb-4">
        <RoleToggle selectedRole="Claimant" />
        <Button
          onClick={toggleModal}
          className="flex items-center space-x-2 rounded bg-indigo-500 p-4 text-white hover:bg-indigo-600"
        >
          <span>Preview Invites and Reminders</span>
          <SendIcon className="h-5 w-5" />
        </Button>
      </div>
      <TabGroup>
        <TabList className="flex space-x-8" aria-label="Tabs">
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            <span>All Dates (13)</span>
          </Tab>
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            <span>Pick a date (10)</span>
          </Tab>
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            <span>Needs Verification (3)</span>
          </Tab>
        </TabList>
        <TabPanels className="mt-5">
          <TabPanel className="rounded-xl bg-white p-5">
            <div className="mb-0 flex w-full">
              <main className="w-5/12 bg-white p-4">
                <ViewerWrapper>
                  <div
                    style={{
                      border: "1px solid rgba(0, 0, 0, 0.3)",
                      height: "90vh",
                    }}
                  >
                    <Toolbar />
                    <Viewer fileUrl="/demo.pdf" plugins={[highlightPluginInstance, toolbarPluginInstance]} />
                  </div>
                </ViewerWrapper>
              </main>
              <aside className="w-7/12 bg-white p-4 shadow">
                <h2 className="mb-4 text-lg font-bold">Verification</h2>
                <TabGroup>
                  <TabList className="flex space-x-8" aria-label="Tabs">
                    <Tab
                      className={({ selected }) =>
                        `group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none ${tabClassName(selected)}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <BellIcon className={`-ml-0.5 mr-2 h-5 w-5 ${iconClassName(selected)}`} aria-hidden="true" />
                          <span>All dates (13)</span>
                        </>
                      )}
                    </Tab>
                  </TabList>
                  <TabPanels className="mt-5">
                    <TabPanel className="rounded-xl bg-white p-2">
                      <AllDatesTable courtData={courtDataUpdatedMock} handleHighlight={handleHighlight} />
                    </TabPanel>
                    <TabPanel className="rounded-xl bg-white p-5 shadow-md">
                      <h3 className="mb-5 text-2xl font-semibold">REJECTED</h3>
                      <p>Reference dates content goes here.</p>
                    </TabPanel>
                    <TabPanel className="rounded-xl bg-white p-5 shadow-md">
                      <h3 className="mb-5 text-2xl font-semibold">Other</h3>
                      <p>Other content goes here.</p>
                    </TabPanel>
                  </TabPanels>
                </TabGroup>
              </aside>
            </div>
          </TabPanel>
          <TabPanel className="rounded-xl bg-white p-5">
            <div className="mb-0 w-full items-center">
              <DeadlineReminderListTable events={CourtEventsMockData} />
            </div>
          </TabPanel>
          <TabPanel className="rounded-xl bg-white p-5">
            <div className="mb-0 flex w-full">
              <main className="w-5/12 bg-white p-4">
                <ViewerWrapper>
                  <div
                    style={{
                      border: "1px solid rgba(0, 0, 0, 0.3)",
                      height: "90vh",
                    }}
                  >
                    <Toolbar />
                    <Viewer fileUrl="/demo.pdf" plugins={[highlightPluginInstance, toolbarPluginInstance]} />
                  </div>
                </ViewerWrapper>
              </main>
              <aside className="w-7/12 bg-white p-4 shadow">
                <h2 className="mb-4 text-lg font-bold">Verification</h2>
                <TabGroup>
                  <TabList className="flex space-x-8" aria-label="Tabs">
                    <Tab
                      className={({ selected }) =>
                        `group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none ${tabClassName(selected)}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <BellIcon className={`-ml-0.5 mr-2 h-5 w-5 ${iconClassName(selected)}`} aria-hidden="true" />
                          <span>TO VERIFY (3)</span>
                        </>
                      )}
                    </Tab>
                    <Tab
                      className={({ selected }) =>
                        `group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none ${tabClassName(selected)}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <CalendarIcon
                            className={`-ml-0.5 mr-2 h-5 w-5 ${iconClassName(selected)}`}
                            aria-hidden="true"
                          />
                          <span>REJECTED (0)</span>
                        </>
                      )}
                    </Tab>
                  </TabList>
                  <TabPanels className="mt-5">
                    <TabPanel className="rounded-xl bg-white p-2">
                      <VerifyTable courtData={courtDataUpdatedMock} handleHighlight={handleHighlight} />
                    </TabPanel>
                    <TabPanel className="rounded-xl bg-white p-5 shadow-md">
                      <h3 className="mb-5 text-2xl font-semibold">REJECTED</h3>
                      <p>Reference dates content goes here.</p>
                    </TabPanel>
                    <TabPanel className="rounded-xl bg-white p-5 shadow-md">
                      <h3 className="mb-5 text-2xl font-semibold">Other</h3>
                      <p>Other content goes here.</p>
                    </TabPanel>
                  </TabPanels>
                </TabGroup>
              </aside>
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
      <div className="mt-6 flex justify-end">
        <Button
          onClick={toggleModal}
          className="flex items-center space-x-2 rounded bg-indigo-500 p-4 text-white hover:bg-indigo-600"
        >
          <span>Preview Invites and Reminders</span>
          <SendIcon className="h-5 w-5" />
        </Button>
      </div>
      <PreviewInvitesModal isOpen={isOpen} toggleModal={toggleModal} />
    </div>
  );
};
